/**
 * Per-user desk session hints (localStorage v1).
 * Keys: lastLoginAt, previousLoginAt, lastDealId, lastRoute.
 * CA-safe — metadata only; no economics.
 */

export const DESK_SESSION_PREFIX = 'd2d.energy.deskSession.';

export function deskSessionStorageKey(userId) {
  if (userId == null || userId === '') return null;
  return `${DESK_SESSION_PREFIX}${userId}`;
}

function safeParse(raw) {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
}

/** Read persisted desk session for a user (browser only). */
export function readDeskSession(userId) {
  const key = deskSessionStorageKey(userId);
  if (!key || typeof window === 'undefined' || !window.localStorage) {
    return {
      lastLoginAt: null,
      previousLoginAt: null,
      lastDealId: null,
      lastRoute: null,
    };
  }
  try {
    const parsed = safeParse(window.localStorage.getItem(key)) || {};
    return {
      lastLoginAt: typeof parsed.lastLoginAt === 'string' ? parsed.lastLoginAt : null,
      previousLoginAt: typeof parsed.previousLoginAt === 'string' ? parsed.previousLoginAt : null,
      lastDealId: typeof parsed.lastDealId === 'string' ? parsed.lastDealId : null,
      lastRoute: typeof parsed.lastRoute === 'string' ? parsed.lastRoute : null,
    };
  } catch {
    return {
      lastLoginAt: null,
      previousLoginAt: null,
      lastDealId: null,
      lastRoute: null,
    };
  }
}

function writeDeskSession(userId, next) {
  const key = deskSessionStorageKey(userId);
  if (!key || typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * On authenticated session start: shift lastLoginAt → previousLoginAt and stamp now.
 * Idempotent within the same calendar day so hash refreshes do not churn the stamp.
 */
export function touchLoginAt(userId, now = new Date()) {
  if (userId == null) return readDeskSession(userId);
  const cur = readDeskSession(userId);
  const iso = now.toISOString();
  const sameDay =
    cur.lastLoginAt &&
    new Date(cur.lastLoginAt).toDateString() === now.toDateString();
  if (sameDay) return cur;
  const next = {
    ...cur,
    previousLoginAt: cur.lastLoginAt || null,
    lastLoginAt: iso,
  };
  writeDeskSession(userId, next);
  return next;
}

/** Persist last deal + hash route (path only, e.g. overview / decision / abs/summary). */
export function writeLastDeskLocation(userId, { dealId = null, route = null } = {}) {
  if (userId == null) return readDeskSession(userId);
  const cur = readDeskSession(userId);
  const next = {
    ...cur,
    lastDealId: dealId || cur.lastDealId,
    lastRoute: route || cur.lastRoute,
  };
  writeDeskSession(userId, next);
  return next;
}

/** morning | afternoon | evening — local clock. */
export function timeOfDayGreeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export function firstNameFromUser(user) {
  const name = String(user?.name || '').trim();
  if (!name) {
    const email = String(user?.email || '').trim();
    if (email.includes('@')) return email.split('@')[0];
    return 'there';
  }
  return name.split(/\s+/)[0];
}

/** Strip hash query; map #projects → null (not a desk route to resume). */
export function normalizeDeskRoute(hashOrPath) {
  const raw = String(hashOrPath || '')
    .replace(/^#/, '')
    .split('?')[0]
    .replace(/^\/+|\/+$/g, '');
  if (!raw || raw === 'projects' || raw === 'deals') return null;
  return raw;
}
